import { useCallback } from 'react';
import { CreateTaskRequest, UpdateTaskRequest, EnhancedDashboardTask } from '../types/api';
import LexAIService from '../services/api';

interface UseTaskOperationsReturn {
  createTask: (request: CreateTaskRequest) => Promise<EnhancedDashboardTask | null>;
  updateTask: (taskId: string, updates: UpdateTaskRequest) => Promise<EnhancedDashboardTask | null>;
  deleteTask: (taskId: string) => Promise<boolean>;
  completeTask: (taskId: string, taskData: EnhancedDashboardTask[]) => Promise<boolean>;
}

export const useTaskOperations = (): UseTaskOperationsReturn => {
  const createTask = useCallback(async (request: CreateTaskRequest): Promise<EnhancedDashboardTask | null> => {
    try {
      const newTask = await LexAIService.createTask(request);
      return newTask;
    } catch (error) {
      console.error('❌ Failed to create task:', error);
      return null;
    }
  }, []);

  const updateTask = useCallback(async (taskId: string, updates: UpdateTaskRequest): Promise<EnhancedDashboardTask | null> => {
    try {
      const updatedTask = await LexAIService.updateTask(taskId, updates);
      return updatedTask;
    } catch (error) {
      console.error('❌ Failed to update task:', error);
      return null;
    }
  }, []);

  const deleteTask = useCallback(async (taskId: string): Promise<boolean> => {
    try {
      const success = await LexAIService.deleteTask(taskId);
      return success;
    } catch (error) {
      console.error('❌ Failed to delete task:', error);
      return false;
    }
  }, []);

  const completeTask = useCallback(async (taskId: string, taskData: EnhancedDashboardTask[]): Promise<boolean> => {
    try {
      const task = taskData.find(t => t.id === taskId);
      if (!task) return false;

      // Update task to completed
      const updates = {
        isCompleted: true,
        status: 'Completed'
      };

      const success = await LexAIService.updateTask(task.id, updates);
      return success !== null;
    } catch (error) {
      console.error('❌ Failed to complete task:', error);
      return false;
    }
  }, []);

  return {
    createTask,
    updateTask,
    deleteTask,
    completeTask
  };
};
